import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";
import { VerifyChip } from "../components/DataLabel";

const num = (n) => Math.round(n || 0).toLocaleString();

/** The county land register: what we can verify, and where we need the counties to fill it in. */
export default function LandAudit() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");

  useSeo({
    title: "County Land Audit — KickStartKenyaFootball",
    description:
      "Public grounds and stadia across the 47 counties, how much land each holds and what football formats would fit — with every unverified figure labelled and open to correction.",
  });

  useEffect(() => {
    api.get("/land-audit").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  const rows = useMemo(
    () => (data ? data.rows.filter((r) => r.county.toLowerCase().includes(q.toLowerCase())) : []),
    [data, q],
  );

  if (!data) return <div className="min-h-[60vh] bg-black" data-testid="land-audit-loading" />;

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="land-audit-page">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <Reveal>
          <p className="overline text-ochre">County land audit</p>
          <h1 className="display-1 mt-5 max-w-4xl">Where The Land Already Is</h1>
          <p className="body-copy mt-6 max-w-3xl">
            Kenya publishes no central register of county land available for sport. So this is built from what we
            can name and check — national and county stadia, municipal grounds — and it is open to correction by
            the people who can see the land.
          </p>
          <p className="mono mt-6 flex max-w-3xl items-start gap-3 border-l-2 border-zinc-700 bg-[#0b0b0b] p-5 text-[0.6rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-400">
            <MapPin size={16} className="mt-0.5 shrink-0 text-zinc-500" />
            <span>
              <VerifyChip v="unverified" /> A register of land, not of title. Nothing here confirms ownership,
              tenure or planning consent.
            </span>
          </p>
        </Reveal>

        <dl className="mono mt-12 grid gap-3 sm:grid-cols-4" data-testid="land-totals">
          {[
            ["Grounds recorded", data.totals.venues],
            ["Counties covered", `${data.totals.counties_with_a_recorded_venue} of 47`],
            ["Land recorded", `${num(data.totals.recorded_area_acres)} acres`],
            ["Counties with nothing yet", data.totals.counties_with_none],
          ].map(([k, v]) => (
            <div key={k} className="border border-zinc-900 bg-[#0b0b0b] p-5">
              <dt className="text-[0.5rem] uppercase tracking-[0.18em] text-zinc-600">{k}</dt>
              <dd className="mt-2 font-display text-xl text-white">{v}</dd>
            </div>
          ))}
        </dl>

        <div className="mt-10 flex flex-wrap items-end gap-4">
          <label className="block">
            <span className="mono block text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">Find a county</span>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="e.g. Kisumu"
              data-testid="land-search"
              className="mt-2 w-56 border border-white/20 bg-transparent px-3.5 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-ochre focus:outline-none"
            />
          </label>
          <Link to={data.submit} className="btn-primary inline-flex items-center gap-2" data-testid="land-submit-cta">
            Tell us about land we have missed <ArrowRight size={14} />
          </Link>
        </div>

        <div className="mt-10 space-y-px bg-zinc-800" data-testid="land-rows">
          {rows.map((r) => (
            <div key={r.county} className="bg-[#0b0b0b] p-5 md:p-6" data-testid={`land-county-${r.county.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <h2 className="font-display text-lg uppercase tracking-tight text-white md:text-xl">{r.county}</h2>
                <p className="mono text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">
                  {r.venue_count} ground{r.venue_count === 1 ? "" : "s"} · {num(r.known_area_acres)} acres recorded
                  {" · "}{num(r.population)} people
                </p>
              </div>
              <div className="mt-4 grid gap-px bg-zinc-900 sm:grid-cols-2 lg:grid-cols-3">
                {r.venues.map((v) => (
                  <div key={v.name} className="bg-black p-4" data-testid={`land-venue-${v.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <p className="text-sm leading-snug text-white">{v.name}</p>
                    <p className="mono mt-1 text-[0.5rem] uppercase tracking-[0.16em] text-zinc-500">{v.type}</p>
                    <p className="mono mt-2 text-[0.55rem] uppercase tracking-[0.14em] text-ochre">
                      {num(v.area_m2)} m² · {v.area_acres} ac
                    </p>
                    <span className="mt-2 block">
                      <VerifyChip v={v.verification} />
                    </span>
                    <p className="mono mt-3 text-[0.5rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-600">
                      Would take: {v.fits.filter((f) => f.count > 0).slice(0, 3).map((f) => `${f.count} x ${f.format}`).join(" · ") || "nothing at full size"}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <section className="mt-16" data-testid="land-method">
          <p className="eyebrow">Method and limits</p>
          <ul className="mt-6 max-w-4xl space-y-4">
            {data.method.map((m) => (
              <li key={m} className="border-l-2 border-zinc-800 pl-5 text-sm leading-relaxed text-zinc-300">{m}</li>
            ))}
          </ul>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/apply" className="btn-primary" data-testid="land-apply-cta">File a site as a proposal</Link>
            <Link to="/facility-planner" className="btn-ghost">Cost a facility</Link>
            <Link to="/pitch-sizing" className="btn-ghost">Pitch sizing</Link>
            <Link to="/sources" className="btn-ghost">Corrections log</Link>
          </div>
        </section>
      </div>
    </div>
  );
}
